// file: client/src/writer/components/ChapterDetailsForm.tsx

import React, { useState } from 'react';
import { ChapterDetailEdits, LoadingStates, ChapterVitals, Chapter } from '../types.js'; // Added Chapter
import { labelClass, inputClass, textareaClass, actionButtonClass, primaryButtonClass, secondaryButtonClass } from '../constants.js';
import { useAppContext } from '../contexts/AppContext.js';

interface ChapterDetailsFormProps {
    detailEdits: Partial<ChapterDetailEdits> | null; 
    activeChapterForPreview: Chapter | null | undefined; // Added to access previewContent/Summary
    onDetailInputChange: (field: keyof Omit<ChapterDetailEdits, 'vitals'>, value: string) => void;
    onVitalsInputChange: (field: keyof ChapterVitals, value: string) => void; 
    onSaveChapterDetails: () => void;
    onGenerateChapterPreview: () => void; // Renamed from onGenerateInitialText
    isLoading: LoadingStates;
    areDetailsUnsaved: boolean; 
    chapterPreviewPercentage: number; // Added
    onChapterPreviewPercentageChange: (percentage: number) => void; // Added
}

const ChapterDetailsForm: React.FC<ChapterDetailsFormProps> = ({
    detailEdits, activeChapterForPreview, onDetailInputChange, onVitalsInputChange, onSaveChapterDetails, 
    onGenerateChapterPreview, isLoading, areDetailsUnsaved,
    chapterPreviewPercentage, onChapterPreviewPercentageChange
}) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [activeEditSection, setActiveEditSection] = useState<'details' | 'vitals' | null>(null);
    
    if (!detailEdits || !activeChapterForPreview) return null; 

    // Check if we have a valid word count for generation
    const expectedWordCount = parseInt(detailEdits?.expectedWordCount || activeChapterForPreview?.expectedWordCount?.toString() || '0', 10);
    const hasValidWordCount = expectedWordCount > 0;
    
    // DEBUGGING - Log all conditions
    console.log('🔍 Button Debug Info:', {
        'detailEdits?.expectedWordCount': detailEdits?.expectedWordCount,
        'activeChapterForPreview?.expectedWordCount': activeChapterForPreview?.expectedWordCount,
        'expectedWordCount': expectedWordCount,
        'hasValidWordCount': hasValidWordCount,
        'isLoading[genPreview]': isLoading['genPreview'],
        'isLoading object': isLoading,
        '!detailEdits': !detailEdits,
        '!hasValidWordCount': !hasValidWordCount,
        'areDetailsUnsaved': areDetailsUnsaved
    });
    
    // Only disable if actively loading, no details exist, invalid word count, or content already exists
    // Use strict comparison to ensure boolean true, not string "false"
    const isGeneratingPreview = isLoading?.genPreview === true;
    const hasExistingContent = !!(activeChapterForPreview?.previewContent && activeChapterForPreview.previewContent.length > 0);
    const isGenerateDisabled = isGeneratingPreview || !detailEdits || !hasValidWordCount || hasExistingContent;
    
    console.log('🔍 Final state:', {
        'isGeneratingPreview': isGeneratingPreview,
        'hasExistingContent': hasExistingContent,
        'previewContentLength': activeChapterForPreview?.previewContent?.length || 0,
        'isGenerateDisabled': isGenerateDisabled,
        'button should be': isGenerateDisabled ? 'DISABLED' : 'ENABLED'
    });
    
    let generateButtonTitle = "";
    if (isGeneratingPreview) {
        generateButtonTitle = "Currently generating preview...";
    } else if (!detailEdits) {
        generateButtonTitle = "Chapter details are not available.";
    } else if (!hasValidWordCount) {
        generateButtonTitle = "Please set a valid expected word count for the chapter first.";
    } else if (hasExistingContent) {
        generateButtonTitle = "Preview already generated for this chapter. Check the editor or preview section above.";
    } else if (areDetailsUnsaved) {
        generateButtonTitle = "Will generate preview with current (unsaved) details. Save first if you want to persist changes.";
    } else {
        generateButtonTitle = "Generate a short preview of this chapter";
    }

    const vitals = detailEdits?.vitals || {};

    // Helper function to handle edit mode toggling
    const handleEditToggle = (section: 'details' | 'vitals' | null) => {
        if (activeEditSection === section) {
            setActiveEditSection(null);
            setIsEditMode(false);
        } else {
            setActiveEditSection(section);
            setIsEditMode(true);
        }
    };

    // Helper function to handle save and exit edit mode
    const handleSaveAndExit = () => {
        onSaveChapterDetails();
        setIsEditMode(false);
        setActiveEditSection(null);
    };

    // Helper function to cancel edit mode
    const handleCancelEdit = () => {
        setIsEditMode(false);
        setActiveEditSection(null);
    };

    // Helper function to format display value
    const formatDisplayValue = (value: string | number | undefined) => {
        if (!value) return <span className="text-gray-500 italic">Not specified</span>;
        return <div className="whitespace-pre-wrap">{value}</div>;
    };

    const vitalFields: Array<{ key: keyof ChapterVitals; label: string; placeholder: string, info?: string }> = [
        { key: 'chapterStartConcept', label: 'Chapter Start Concept:', placeholder: 'How does this chapter begin? e.g., A tense negotiation...' },
        { key: 'chapterEndConcept', label: 'Chapter End Concept:', placeholder: 'How does this chapter conclude? e.g., Ends on a cliffhanger as...' },
        { key: 'objective', label: 'Chapter Objective:', placeholder: 'Main goal or purpose of this chapter. e.g., Introduce the antagonist, reveal a clue...' },
        { 
            key: 'keyCharactersInChapter', 
            label: 'Key Characters in Chapter:', 
            placeholder: 'List characters central to this chapter and their roles. e.g., Elara (protagonist, makes a crucial decision), Kael (support, provides exposition)',
            info: "Tip: For consistency, try to use character names already defined in your 'World Building' section. You can add new, chapter-specific names here, but consider if they should also be added to the global World Building data for broader use."
        },
        { 
            key: 'temparoryCharactersInChapter', 
            label: 'Temporary Characters in Chapter:', 
            placeholder: 'temporary characters, who may reappear in some other chapters and their specific roles in THIS chapter (e.g., "Ellaya (A street vendor, having a small buisiness on kingdom streets), Mallika (A beutiful young lady in city, who is very fond of prince)"',
            info: "Tip: For consistency, try to use character names already defined in your 'World Building' section. You can add new, chapter-specific names here, but consider if they should also be added to the global World Building data for broader use."
        },
        { key: 'intenseScenes', label: 'Intense Scenes / Highlights:', placeholder: 'Describe any high-stakes or climactic moments. e.g., The duel at sunset; the discovery of the hidden passage.' },
        { key: 'characterEvolutionInChapter', label: 'Character Evolution in Chapter:', placeholder: 'How characters change or develop. e.g., Elara overcomes her fear; Kael starts to trust the group.' },
        { 
            key: 'keyLocationsInChapter', 
            label: 'Key Locations in Chapter (and their role):', 
            placeholder: 'Specific places relevant to this chapter. e.g., The Shadow Market - for clandestine meetings; The Oracle\'s Peak - for a revelation.',
            info: "Tip: For consistency, try to use location names already defined in your 'World Building' section. You can add new, chapter-specific names here, but consider if they should also be added to the global World Building data for broader use."
        },
        { key: 'culturalElementsInChapter', label: 'Cultural Elements / Details for this Chapter:', placeholder: 'Festivals, customs, rituals, specific flora/fauna to mention. e.g., The annual Moon Festival is underway; characters eat Glowberries unique to this region.' },
        { key: 'authorsDirection', label: "Author's Core Direction for this Chapter:", placeholder: "Key focus, tone, or specific instructions for the AI when generating this chapter's content. This will be given high priority by the AI.", info: "This direction will heavily influence AI content generation for this chapter." },
    ];

    const appContext = useAppContext();
    const { projectSetupLogic } = appContext || {};

    return (
        <div >
            <h2 className="text-xl font-semibold mb-3">Chapter Details & Preview Generation</h2>
            
            {/* Word Count Fix Alert */}
            {!hasValidWordCount && (
                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                            <div className="w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">!</span>
                            </div>
                            <div>
                                <p className="text-sm font-medium text-yellow-800">Invalid Expected Word Count</p>
                                <p className="text-xs text-yellow-700">
                                    This chapter has no expected word count set. Click below to automatically calculate word counts for all chapters.
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => projectSetupLogic?.fixChapterWordCounts?.()}
                            className="px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700 transition-colors"
                        >
                            Fix Word Counts
                        </button>
                    </div>
                </div>
            )}
            
            {/* Chapter Basic Details Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">Chapter Details</h3>
                    {activeEditSection !== 'details' && (
                        <button
                            onClick={() => handleEditToggle('details')}
                            className={`${actionButtonClass} px-3 py-1 text-sm`}
                        >
                            <svg className="w-4 h-4 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                        </button>
                    )}
                </div>

                {activeEditSection === 'details' ? (
                    <div className="space-y-4">
                        <div><label className={labelClass} htmlFor="chapTitle">Title:</label><input id="chapTitle" type="text" value={activeChapterForPreview.title || ''} onChange={e => onDetailInputChange('title', e.target.value)} className={inputClass} /></div>
                        <div><label className={labelClass} htmlFor="chapSummary">Initial Summary (from Outline):</label><textarea id="chapSummary" value={detailEdits.summary || ''} onChange={e => onDetailInputChange('summary', e.target.value)} className={textareaClass} /></div>
                        <div><label className={labelClass} htmlFor="chapGeo">Geography:</label><textarea id="chapGeo" value={detailEdits.geography || ''} onChange={e => onDetailInputChange('geography', e.target.value)} className={textareaClass} /></div>
                        <div><label className={labelClass} htmlFor="chapChars">Characters Involved (Overall):</label><textarea id="chapChars" value={detailEdits.charactersInvolved || ''} onChange={e => onDetailInputChange('charactersInvolved', e.target.value)} className={textareaClass} /></div>
                        <div><label className={labelClass} htmlFor="chapActions">Character Actions (Overall):</label><textarea id="chapActions" value={detailEdits.characterActions || ''} onChange={e => onDetailInputChange('characterActions', e.target.value)} className={textareaClass} /></div>
                        <div><label className={labelClass} htmlFor="chapSoul">Soul of Chapter (Overall):</label><textarea id="chapSoul" value={detailEdits.soulOfChapter || ''} onChange={e => onDetailInputChange('soulOfChapter', e.target.value)} className={textareaClass} /></div>
                        <div><label className={labelClass} htmlFor="chapWords">Expected Word Count (for full chapter):</label><input id="chapWords" type="number" value={detailEdits.expectedWordCount || ''} onChange={e => onDetailInputChange('expectedWordCount', e.target.value)} className={inputClass} /></div>
                        
                        <div className="flex space-x-3 pt-4">
                            <button onClick={handleSaveAndExit} className={`${primaryButtonClass} flex-1`}>Save Changes</button>
                            <button onClick={handleCancelEdit} className={`${secondaryButtonClass} flex-1`}>Cancel</button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <h4 className="font-medium text-gray-700 mb-1">Title</h4>
                                <div className="text-gray-900">{formatDisplayValue(activeChapterForPreview.title)}</div>
                            </div>
                            <div>
                                <h4 className="font-medium text-gray-700 mb-1">Expected Word Count</h4>
                                <div className="text-gray-900">{formatDisplayValue(detailEdits.expectedWordCount)}</div>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-700 mb-1">Summary</h4>
                            <div className="text-gray-900 bg-gray-50 p-3 rounded">{formatDisplayValue(detailEdits.summary)}</div>
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-700 mb-1">Geography</h4>
                            <div className="text-gray-900 bg-gray-50 p-3 rounded">{formatDisplayValue(detailEdits.geography)}</div>
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-700 mb-1">Characters Involved</h4>
                            <div className="text-gray-900 bg-gray-50 p-3 rounded">{formatDisplayValue(detailEdits.charactersInvolved)}</div>
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-700 mb-1">Character Actions</h4>
                            <div className="text-gray-900 bg-gray-50 p-3 rounded">{formatDisplayValue(detailEdits.characterActions)}</div>
                        </div>
                        <div>
                            <h4 className="font-medium text-gray-700 mb-1">Soul of Chapter</h4>
                            <div className="text-gray-900 bg-gray-50 p-3 rounded">{formatDisplayValue(detailEdits.soulOfChapter)}</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Chapter Vitals Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">Chapter Vitals</h3>
                        <p className="text-sm text-gray-600">Specific details for this chapter used by AI for content generation</p>
                    </div>
                    {activeEditSection !== 'vitals' && (
                        <button
                            onClick={() => handleEditToggle('vitals')}
                            className={`${actionButtonClass} px-3 py-1 text-sm`}
                        >
                            <svg className="w-4 h-4 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                        </button>
                    )}
                </div>

                {activeEditSection === 'vitals' ? (
                    <div className="space-y-4">
                        {vitalFields.map(field => (
                            <div key={String(field.key)}>
                                <label htmlFor={`vital-${String(field.key)}`} className={labelClass}>{field.label}</label>
                                <textarea
                                    id={`vital-${String(field.key)}`}
                                    value={String(vitals[field.key as keyof ChapterVitals] || '')}
                                    onChange={e => onVitalsInputChange(field.key, e.target.value)}
                                    placeholder={field.placeholder}
                                    className={`${textareaClass} min-h-[60px]`}
                                    rows={field.key === 'authorsDirection' ? 4 : 3}
                                />
                                {field.info && <p className="text-xs text-gray-500 mt-1">{field.info}</p>}
                            </div>
                        ))}
                        
                        <div className="flex space-x-3 pt-4">
                            <button onClick={handleSaveAndExit} className={`${primaryButtonClass} flex-1`}>Save Changes</button>
                            <button onClick={handleCancelEdit} className={`${secondaryButtonClass} flex-1`}>Cancel</button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {vitalFields.map(field => (
                            <div key={String(field.key)} className="col-span-1">
                                <h4 className="font-medium text-gray-700 mb-1">{field.label}</h4>
                                <div className="text-gray-900 bg-gray-50 p-3 rounded text-sm">
                                    {formatDisplayValue(vitals[field.key as keyof ChapterVitals])}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Preview Generation Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Generate Short Chapter Preview (AI)</h3>
                <p className="text-sm text-gray-600 mb-4">Uses saved details & vitals. Generates a short text preview to check AI direction before generating full content.</p>
                
                {isGenerateDisabled && !isGeneratingPreview && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-amber-800">
                                    <strong>Cannot generate preview:</strong> {generateButtonTitle}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                
                <div className="flex items-center space-x-3 mb-4">
                    <label htmlFor="previewPercentage" className={labelClass}>Preview Length:</label>
                    <select 
                        id="previewPercentage"
                        value={chapterPreviewPercentage} 
                        onChange={(e) => onChapterPreviewPercentageChange(Number(e.target.value))}
                        className={`${inputClass} w-auto`}
                        disabled={isGenerateDisabled}
                    >
                        <option value={10}>10%</option>
                        <option value={25}>25%</option>
                        <option value={50}>50%</option>
                    </select>
                    <button 
                        key={`preview-btn-${isGenerateDisabled}-${isGeneratingPreview}`}
                        onMouseEnter={() => console.log('🟡 Button mouse enter - enabled:', !isGenerateDisabled)}
                        onMouseDown={() => console.log('🟠 Button mouse down')}
                        onMouseUp={() => console.log('🟢 Button mouse up')}
                        onClick={(e) => {
                            console.log('🔴 BUTTON CLICKED!', { 
                                timestamp: new Date().toISOString(),
                                isGenerateDisabled, 
                                isGeneratingPreview, 
                                onGenerateChapterPreview: typeof onGenerateChapterPreview,
                                buttonDisabled: e.currentTarget.disabled,
                                event: e.type
                            });
                            
                            if (isGenerateDisabled) {
                                console.warn('⚠️ Button is disabled, click should not have triggered');
                                return;
                            }
                            
                            if (onGenerateChapterPreview) {
                                try {
                                    onGenerateChapterPreview();
                                } catch (error) {
                                    console.error('❌ Error calling onGenerateChapterPreview:', error);
                                }
                            } else {
                                console.error('❌ onGenerateChapterPreview is not defined!');
                            }
                        }} 
                        disabled={isGenerateDisabled} 
                        className={`${primaryButtonClass} flex-grow ${isGenerateDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${!isGenerateDisabled ? 'border-2 border-green-500 shadow-lg' : ''}`}
                        title={generateButtonTitle}
                        style={{ 
                            pointerEvents: isGenerateDisabled ? 'none' : 'auto',
                            position: 'relative',
                            zIndex: 10,
                            backgroundColor: !isGenerateDisabled ? '#059669' : undefined
                        }}
                    >
                        {isGeneratingPreview ? 'Generating Preview...' : 'Generate Short Preview'}
                    </button>
                </div>

                {activeChapterForPreview?.previewContent && (
                    <div className="mt-4 p-4 border border-blue-200 bg-blue-50 rounded-md">
                        <h4 className="text-sm font-semibold text-blue-700 mb-1">AI Generated Preview (approx. {activeChapterForPreview.previewWordCountTarget} words):</h4>
                        {activeChapterForPreview.previewSummary && (
                             <p className="text-xs italic text-blue-600 mb-2">Preview Summary: {activeChapterForPreview.previewSummary}</p>
                        )}
                        <div className="prose prose-sm max-w-none max-h-60 overflow-y-auto p-2 bg-white border border-blue-300 rounded" dangerouslySetInnerHTML={{ __html: activeChapterForPreview.previewContent }}></div>
                        <p className="text-xs text-blue-600 mt-2">Review this preview. If satisfied, go to the 'Content & Revisions' tab to generate the full chapter text.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChapterDetailsForm;
